import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';

// Title Options Component - Interactive version with dynamic bar chart
export const TitleOptions = ({
  titleKeys = [],
  advanceToNextTopic,
  setMessages,
  onSendText,
  messages = [],
  handleRefreshResults,
  onTitlesSelected
}) => {
  const [allTitles, setAllTitles] = useState([]); // Full sorted list of titles
  const [displayedTitles, setDisplayedTitles] = useState([]); // Currently displayed titles (max 5)
  const [selectedTitles, setSelectedTitles] = useState([]); // Selected titles
  const [titleData, setTitleData] = useState([]); // Processed data for display
  
  useEffect(() => {
    // Initialize with the provided titleKeys
    if (titleKeys && titleKeys.length > 0) {
      console.log('TitleOptions: Using titleKeys with', titleKeys.length, 'titles');
      
      // Store all titles sorted by rank (already sorted from strategy)
      setAllTitles([...titleKeys]);
      
      // Initially display top 5 titles
      const initialDisplayed = titleKeys.slice(0, 5);
      setDisplayedTitles(initialDisplayed);
      
      // Auto-select ALL displayed titles by default
      setSelectedTitles([...initialDisplayed]);
      
      // Process the initial data
      processTitleData(initialDisplayed);
    }
  }, [titleKeys]);
  
  useEffect(() => {
    // Update title data whenever displayed titles change
    if (displayedTitles.length > 0) {
      processTitleData(displayedTitles);
    }
  }, [displayedTitles]);
  
  /**
   * Process the displayed titles to create the bar chart data
   * @param {Array} titles - The titles to display
   */
  const processTitleData = (titles) => {
    // Create data for the displayed titles
    const titlesWithData = titles.map((title, index) => ({
      title,
      // Use position in the original sorted list for consistent percentages
      originalIndex: allTitles.indexOf(title),
      // Calculate count based on original position (higher rank = higher count)
      count: Math.max(100 - (allTitles.indexOf(title) * 10), 10)
    }));
    
    // Calculate the total count for percentage calculation
    const totalCount = titlesWithData.reduce((sum, item) => sum + item.count, 0);
    
    // Calculate percentages
    const titlesWithPercentages = titlesWithData.map(item => ({
      ...item,
      percentage: Math.round((item.count / totalCount) * 100)
    }));
    
    // Find the highest percentage to use as the base for relative widths
    const maxPercentage = Math.max(...titlesWithPercentages.map(item => item.percentage));
    
    // Calculate relative widths (highest percentage gets 100% width, others are relative)
    const titlesWithRelativeWidths = titlesWithPercentages.map(item => ({
      ...item,
      relativeWidth: Math.round((item.percentage / maxPercentage) * 100)
    }));
    
    setTitleData(titlesWithRelativeWidths);
  };
  
  /**
   * Handle title selection/deselection with dynamic chart updates
   * @param {string} title - The title to toggle selection
   */
  const handleTitleSelection = (title) => {
    setSelectedTitles(prev => {
      const newSelected = prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title];
      
      // If a title was deselected, update the displayed titles
      if (prev.includes(title) && !newSelected.includes(title)) {
        updateDisplayedTitles(title, 'remove');
      }
      
      return newSelected;
    });
  };
  
  /**
   * Update the displayed titles when a title is deselected
   * @param {string} removedTitle - The title that was deselected
   * @param {string} action - The action ('remove')
   */
  const updateDisplayedTitles = (removedTitle, action) => {
    if (action === 'remove') {
      setDisplayedTitles(prev => {
        // Remove the deselected title
        const withoutRemoved = prev.filter(t => t !== removedTitle);
        
        // Find the next highest-ranked title that's not already displayed
        const nextTitle = allTitles.find(title => 
          !withoutRemoved.includes(title) && title !== removedTitle
        );
        
        // Add the next title if available and we have less than 5 titles
        if (nextTitle && withoutRemoved.length < 5) {
          const newDisplayed = [...withoutRemoved, nextTitle];
          
          // Auto-select the new title
          setSelectedTitles(current => {
            if (!current.includes(nextTitle)) {
              return [...current, nextTitle];
            }
            return current;
          });
          
          return newDisplayed;
        }
        
        return withoutRemoved;
      });
    }
  };
  
  /**
   * Generate smart confirmation message based on selection
   * @param {Array} selected - Selected titles
   * @param {Array} displayed - Currently displayed titles
   * @returns {Object} - Confirmation data with message and selection info
   */
  const generateSmartConfirmation = (selected, displayed) => {
    const deselectedTitles = displayed.filter(title => !selected.includes(title));
    
    if (selected.length === displayed.length) {
      // All displayed titles are selected
      return {
        message: "Great! The selected job titles look good.",
        type: 'all_selected',
        selectedTitles: selected,
        excludedTitles: deselectedTitles
      };
    } else if (deselectedTitles.length === 1) {
      // Only one title is deselected
      return {
        message: `Perfect! I'll exclude "${deselectedTitles[0]}" from the search.`,
        type: 'exclude_single',
        selectedTitles: selected,
        excludedTitles: deselectedTitles
      };
    } else if (deselectedTitles.length > 1) {
      // Multiple titles are deselected
      const excludeList = deselectedTitles.map(title => `"${title}"`).join(', ');
      return {
        message: `Perfect! I'll exclude ${excludeList} from the search.`,
        type: 'exclude_multiple',
        selectedTitles: selected,
        excludedTitles: deselectedTitles
      };
    } else {
      // Fallback
      return {
        message: "Got it! I'll search for the selected job titles.",
        type: 'fallback',
        selectedTitles: selected,
        excludedTitles: deselectedTitles
      };
    }
  };
  
  /**
   * Handle "Confirm Selection" button click
   */
  const handleConfirm = () => {
    console.log('Titles confirmed:', selectedTitles);
    console.log('Displayed titles:', displayedTitles);
    
    if (onTitlesSelected) {
      // Generate smart confirmation message based on displayed titles
      const confirmationData = generateSmartConfirmation(selectedTitles, displayedTitles);
      
      // Pass both the selected titles and the smart confirmation data
      onTitlesSelected(selectedTitles, confirmationData);
    }
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Main container */}
      <Box
        sx={{
          width: '100%',
          bgcolor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid rgba(192, 197, 205, 0.5)',
          padding: '0px',
          mt: 1,
          mb: 2
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: '20px',
            borderBottom: '1px solid #EAECF0'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: '#101828',
              mb: '2px'
            }}
          >
            Recent Job Titles
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: '#667085',
              lineHeight: '1.2'
            }}
          >
            Employees who recently worked with these job titles
          </Typography>
        </Box>
        
        {/* Chart content */}
        <Box sx={{ padding: '20px' }}>
          {/* Title bars */}
          <Box sx={{ mb: 0 }}>
            {titleData.map((item, index) => {
              const isSelected = selectedTitles.includes(item.title);
              
              return (
                <Box 
                  key={`${item.title}-${index}`} // Use title + index for stable keys
                  sx={{ 
                    mb: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out' // Smooth transition for dynamic updates
                  }}
                  onClick={() => handleTitleSelection(item.title)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      width: `${item.relativeWidth}%`,
                      minWidth: '150px'
                    }}
                  >
                    {/* Title bar */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: isSelected ? '#1C8FFD' : '#E5E7EB',
                        borderRadius: '8px',
                        height: '40px',
                        gap: '4px',
                        padding: '6px 12px',
                        flex: 1,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: isSelected ? '#1C8FFD' : '#D1D5DB'
                        }
                      }}
                    >
                      {/* Check circle icon for selected items */}
                      {isSelected && (
                        <Typography
                          sx={{
                            fontFamily: 'Material Icons Round',
                            fontSize: '16px',
                            color: '#FFFFFF',
                            lineHeight: 1
                          }}
                        >
                          check_circle
                        </Typography>
                      )}
                      
                      {/* Title text */}
                      <Typography
                        sx={{
                          color: isSelected ? '#FFFFFF' : '#374151',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '1.43',
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                    
                    {/* Percentage */}
                    <Typography
                      sx={{
                        color: '#465366',
                        fontWeight: 400,
                        fontSize: '13px',
                        lineHeight: '1.38',
                        textAlign: 'center',
                        minWidth: '40px'
                      }}
                    >
                      {item.percentage}%
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      
      {/* Action button - outside the container */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedTitles.length === 0}
          sx={{
            bgcolor: selectedTitles.length === 0 ? '#C7D2FE' : '#4599FA',
            color: '#FFFFFF',
            fontWeight: 400,
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            height: '36px',
            '&:hover': {
              bgcolor: selectedTitles.length === 0 ? '#C7D2FE' : '#3B7CE8'
            },
            '&.Mui-disabled': {
              bgcolor: '#C7D2FE',
              color: '#FFFFFF'
            }
          }}
        >
          Confirm Selection
        </Button>
      </Box>
    </Box>
  );
};

// Title Confirmation Read-Only Component - For chat history
export const TitleConfirmationReadOnly = ({ titles = [], confirmationData = null }) => {
  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#FFFFFF',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '16px',
        mt: 1
      }}
    >
      {/* Show smart confirmation message if available */}
      {confirmationData ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#101828',
            mb: 2
          }}
        >
          {confirmationData.message}
        </Typography>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#6B7280',
              mb: 2
            }}
          >
            Selected Titles:
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {titles.map((title, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#1C8FFD',
                  borderRadius: '20px',
                  px: 2,
                  py: 0.5,
                  gap: 1
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Material Icons Round',
                    fontSize: '12px',
                    color: '#FFFFFF'
                  }}
                >
                  check_circle
                </Typography>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  {title}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: '#9CA3AF',
              fontStyle: 'italic'
            }}
          >
            Confirmed
          </Typography>
        </>
      )}
    </Box>
  );
};
